import {Controller} from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["checkbox", "schoolWeeksSelect", "holidayWeeksSelect", "checkboxContainer", "rhythmIdInput"]

  /**
   * Selects all enabled checkboxes and resets the week selects.
   */
  selectAll() {
    this.resetWeekSelects()
    this.clearRhythmIdInput()
    this.checkboxTargets.forEach((checkbox) => {
      if (!checkbox.disabled) {
        checkbox.checked = true
        this.dispatchChangeEvent(checkbox)
      }
    })
  }

  /**
   * Selects only even week number checkboxes
   */
  selectEven() {
    this.resetWeekSelects()
    this.clearRhythmIdInput()
    this.uncheckAll()
    this.checkboxTargets.forEach((checkbox) => {
      const weekNumber = parseInt(checkbox.value, 10)
      if (!checkbox.disabled && weekNumber % 2 === 0) {
        checkbox.checked = true
        this.dispatchChangeEvent(checkbox)
      }
    })
  }

  /**
   * Selects only odd week number checkboxes
   */
  selectOdd() {
    this.resetWeekSelects()
    this.clearRhythmIdInput()
    this.uncheckAll()
    this.checkboxTargets.forEach((checkbox) => {
      const weekNumber = parseInt(checkbox.value, 10)
      if (!checkbox.disabled && weekNumber % 2 === 1) {
        checkbox.checked = true
        this.dispatchChangeEvent(checkbox)
      }
    })
  }

  /**
   * Selects weeks based on the data-weeks attribute of the selected option.
   * Resets the other week select dropdown.
   * @param {Event} event - The change event from the select element
   */
  selectByWeeks(event) {
    const selectElement = event.currentTarget
    const selectedOption = selectElement.options[selectElement.selectedIndex]
    const weeksData = selectedOption.dataset.weeks

    this.resetOtherWeekSelect(selectElement)

    if (!weeksData) {
      this.selectNone()
      return
    }

    const weeks = weeksData.split(",").map((w) => parseInt(w, 10))

    this.uncheckAll()
    this.checkboxTargets.forEach((checkbox) => {
      const weekNumber = parseInt(checkbox.value, 10)
      if (!checkbox.disabled && weeks.includes(weekNumber)) {
        checkbox.checked = true
        this.dispatchChangeEvent(checkbox)
      }
    })

    if (this.hasRhythmIdInputTarget) {
      this.rhythmIdInputTarget.value = selectElement.value || ""
    }
  }

  /**
   * Fired when a checkbox is manually changed by the user.
   * Resets both week-select dropdowns and clears the rhythm ID so the
   * manually composed selection is not associated with any preset rhythm.
   * Ignores events dispatched internally by dispatchChangeEvent (marked with
   * _programmatic: true) to avoid resetting state during selectAll/selectByWeeks/etc.
   * @param {Event} event - The change event from the checkbox
   */
  checkboxChanged(event) {
    if (event._programmatic) return

    this.resetWeekSelects()
    this.clearRhythmIdInput()
  }

  /**
   * Unchecks all checkboxes and resets the week selects.
   */
  selectNone() {
    this.resetWeekSelects()
    this.clearRhythmIdInput()
    this.uncheckAll()
  }

  /**
   * Unchecks all checkboxes without resetting week selects.
   */
  uncheckAll() {
    this.checkboxTargets.forEach((checkbox) => {
      checkbox.checked = false
      this.dispatchChangeEvent(checkbox)
    })
  }

  /**
   * Resets both school weeks and holiday weeks select dropdowns to their default (empty) value.
   */
  resetWeekSelects() {
    if (this.hasSchoolWeeksSelectTarget) {
      this.schoolWeeksSelectTarget.value = ""
    }
    if (this.hasHolidayWeeksSelectTarget) {
      this.holidayWeeksSelectTarget.value = ""
    }
  }

  /**
   * Resets the other week select dropdown (not the one that triggered the event).
   * @param {HTMLSelectElement} currentSelect - The select element that triggered the change
   */
  resetOtherWeekSelect(currentSelect) {
    if (this.hasSchoolWeeksSelectTarget && currentSelect !== this.schoolWeeksSelectTarget) {
      this.schoolWeeksSelectTarget.value = ""
    }
    if (this.hasHolidayWeeksSelectTarget && currentSelect !== this.holidayWeeksSelectTarget) {
      this.holidayWeeksSelectTarget.value = ""
    }
  }

  /**
   * Clears the rhythm ID hidden input (used when switching to a manual selection).
   */
  clearRhythmIdInput() {
    if (this.hasRhythmIdInputTarget) {
      this.rhythmIdInputTarget.value = ""
    }
  }

  /**
   * Dispatches a programmatic change event on the checkbox.
   * Marked with _programmatic so checkboxChanged ignores it.
   * @param {HTMLInputElement} checkbox - The checkbox element
   */
  dispatchChangeEvent(checkbox) {
    const event = new Event("change", {bubbles: true})
    event._programmatic = true
    checkbox.dispatchEvent(event)
  }
}
